import { useMemo, useState } from 'react'
import { useFormState } from 'react-final-form'
import type { Column } from 'react-table'
import cx from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'

import Button from 'common/components/button'
import Input from 'common/components/input/Input'
import Loader from 'common/components/loaders/Loader'
import Table from 'common/components/table'
import { ArrowRight as ArrowRightIcon } from 'common/icons'

import type { BrazeCampaign } from './api/api'
import useGetCampaignDetailQuery from './api/queries/useGetCampaignDetailQuery'
import useGetCampaignsQuery from './api/queries/useGetCampaignsQuery'
import useGetCanvasesQuery from './api/queries/useGetCanvasesQuery'
import CampaignDetail from './CampaignDetail'
import MergeTags from './mergeTags'

type ImportType = 'campaigns' | 'canvas'

const COLUMNS: Column<BrazeCampaign>[] = [
  { Header: 'Name', accessor: 'name' },
  { Header: 'Last edited', accessor: 'last_edited', width: 180 },
]

type Props = {
  integration: string
  onSelect?: (campaign: BrazeCampaign | null) => void
  onGenerateContent: (brazeCampaignId: string, mergeTagIds?: string[]) => void
}

const ImportTab = ({ integration, onSelect, onGenerateContent }: Props) => {
  const { showMergeTags } = useFlags()
  const { values } = useFormState({ subscription: { values: true } })

  const [activeType, setActiveType] = useState<ImportType>('campaigns')
  const [search, setSearch] = useState('')
  const [selectedItem, setSelectedItem] = useState<BrazeCampaign | null>(null)

  const {
    data: campaigns = [],
    isLoading: isLoadingCampaigns,
    isError: isCampaignsError,
  } = useGetCampaignsQuery({ integration })

  const {
    data: canvases = [],
    isLoading: isLoadingCanvases,
    isError: isCanvasesError,
  } = useGetCanvasesQuery({ integration })

  const { data: campaignDetail, isLoading: isLoadingDetail } =
    useGetCampaignDetailQuery({
      integration,
      campaignId: activeType === 'campaigns' ? selectedItem?.id ?? null : null,
    })

  const activeItems = activeType === 'campaigns' ? campaigns : canvases
  const isLoading =
    activeType === 'campaigns' ? isLoadingCampaigns : isLoadingCanvases
  const isError =
    activeType === 'campaigns' ? isCampaignsError : isCanvasesError

  const data = useMemo(
    () =>
      activeItems.map((c) => ({ ...c, checked: c.id === selectedItem?.id })),
    [activeItems, selectedItem]
  )

  const handleTypeChange = (type: ImportType) => {
    setActiveType(type)
    setSelectedItem(null)
    setSearch('')
  }

  const handleSelect = (rows: BrazeCampaign[]) => {
    const selected = rows[0] ?? null
    setSelectedItem((prev) => {
      if (prev?.id === selected?.id) {
        return prev
      }
      onSelect?.(selected)
      return selected
    })
  }

  const handleGenerateContent = () => {
    if (!selectedItem) {
      return
    }
    const mergeTagIds = values.merge_tags as string[] | undefined
    onGenerateContent(selectedItem.id, mergeTagIds)
  }

  return (
    <div className="flex flex-col gap-4 pt-9">
      {showMergeTags && <MergeTags />}

      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {(['campaigns', 'canvas'] as ImportType[]).map((type) => (
            <button
              key={type}
              onClick={() => handleTypeChange(type)}
              disabled={type === 'canvas'}
              className={cx(
                'px-3 py-1 text-sm font-medium rounded-full capitalize transition-colors',
                type === 'canvas' && 'opacity-40 cursor-not-allowed',
                activeType === type
                  ? 'bg-maroon-400 text-white'
                  : 'text-base-500 hover:text-base-700'
              )}
            >
              {type}
            </button>
          ))}
        </div>
        <div className="w-[400px] shrink-0">
          <Input
            type="search"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center min-h-96">
          <Loader />
        </div>
      ) : isError || activeItems.length === 0 ? (
        <div className="flex flex-1 items-center justify-center min-h-96 text-base-500 text-sm capitalize">
          No {activeType} available.
        </div>
      ) : (
        <>
          <Table
            columns={COLUMNS}
            data={data}
            rowSelection="single"
            onSelectRow={handleSelect}
            globalFilterValue={search}
            autoResetSelectedRows={false}
            autoResetGlobalFilter={false}
            initialState={{ pageSize: 10 }}
          />
          {activeType === 'campaigns' && campaignDetail && (
            <CampaignDetail
              detail={campaignDetail}
              isLoading={!!selectedItem && isLoadingDetail}
            />
          )}
        </>
      )}

      <div className="flex justify-end pt-4">
        <Button
          variant="primary"
          disabled={!selectedItem}
          suffixIcon={<ArrowRightIcon isDefaultColor={false} size={4} />}
          onClick={handleGenerateContent}
        >
          Generate content
        </Button>
      </div>
    </div>
  )
}

export default ImportTab
